import { config } from '../config/environment'

/**
 * 네이버 광고 전환추적 (wcs.trans)
 * @see https://navercts.gitbook.io/guide/script_guide
 */

export type NaverWcsConversionType =
  | 'lead'
  | 'subscribe'
  | 'view_content'
  | 'sign_up'
  | 'schedule'
  | 'custom001'
  | 'custom002'
  | 'custom003'
  | 'custom004'
  | 'custom005'
  | 'custom006'
  | 'custom007'
  | 'custom008'
  | 'custom009'
  | 'custom010'

declare global {
  interface Window {
    wcs?: {
      inflow: (domain?: string) => void
      trans: (conv: { type: string; [key: string]: unknown }) => void
    }
    wcs_add?: { wa?: string; [key: string]: string | undefined }
    wcs_do?: () => void
  }
}

let initialized = false
let scriptLoading: Promise<void> | null = null

export function getNaverWcsAccountId(): string {
  return config.naverWcs.accountId
}

export function getNaverWcsInflowDomain(): string {
  return config.naverWcs.inflowDomain
}

function ensureAccountConfigured(): boolean {
  const accountId = getNaverWcsAccountId()
  if (!accountId || typeof window === 'undefined') return false

  window.wcs_add = window.wcs_add || {}
  window.wcs_add.wa = accountId
  return true
}

function loadWcsScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.wcs && typeof window.wcs_do === 'function') {
    return Promise.resolve()
  }
  if (scriptLoading) return scriptLoading

  scriptLoading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="wcs.naver.net/wcslog.js"]'
    )
    if (existing) {
      if (window.wcs) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Naver WCS script')),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://wcs.naver.net/wcslog.js'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Naver WCS script'))
    document.head.appendChild(script)
  })

  return scriptLoading
}

/** REACT_APP_NAVER_WCS_ACCOUNT_ID 가 있을 때만 wcslog.js 로드 */
export function initNaverWcs(): void {
  if (!getNaverWcsAccountId() || typeof window === 'undefined' || initialized) return

  initialized = true
  ensureAccountConfigured()
  void loadWcsScript().catch(() => {
    initialized = false
  })
}

/** SPA 라우트 변경 시 PV(wcs_do) 전송 */
export async function trackNaverPageView(): Promise<void> {
  if (!ensureAccountConfigured()) return

  try {
    await loadWcsScript()
  } catch {
    return
  }

  if (!window.wcs || typeof window.wcs_do !== 'function') return

  window.wcs.inflow(getNaverWcsInflowDomain())
  window.wcs_do()
}

/** 전환 이벤트 (문의·구독·콘텐츠 조회 등) */
export async function trackNaverConversion(
  type: NaverWcsConversionType
): Promise<void> {
  if (!ensureAccountConfigured()) return

  try {
    await loadWcsScript()
  } catch {
    return
  }

  if (!window.wcs) return

  window.wcs.trans({ type })
}
